import React, { memo, useState, useMemo, useEffect, useCallback } from 'react';
import { keyBy, isEqual } from 'lodash';
import {
  Card,
  makeStyles,
  Collapse,
  Typography,
  Button,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  ButtonBase,
  IconButton,
  Popover,
} from '@material-ui/core';
import { useDataProvider } from 'react-admin';
import { ExpandLess, ExpandMore, InfoOutlined } from '@material-ui/icons';
import { useDispatch } from 'react-redux';

import { Datagrid } from 'components/list';
import { OrderIdField, PriceField, StoreField } from 'components/fields';
import ShippingServiceField from 'components/fields/ShippingServiceField';
import DeliveryTimeField from 'components/fields/DeliveryTimeField';
import RateEditField from './RateEditField';
import useCustomNotify from 'hooks/useCustomNotify';
import Dialog from 'components/common/Dialog';
import useRefillDialog from 'hooks/useRefillDialog';
import useKycActionGuard from 'hooks/useKycActionGuard';
import formatPrice from 'services/helpers/formatPrice';
import RateStatusField from './RateStatusField';
import { BulkPrintPackingSlipsButton } from './BulkPrintPackingSlipsButton';
import { openDoc } from 'store/actions/lightboxActions';
import { ActionField } from 'components/fields';
import ColumnsConfigButton from 'components/layout/ColumnsConfigButton';
import { PrintPackingSlipActionField } from 'pages/shipments/components/PackingSlip';
import { ReprintLabelActionField } from 'pages/shipments/components/ReprintLabel';
import { ReturnLabelActionField } from 'pages/shipments/components/ReturnLabel';
import { VoidLabelActionField } from 'pages/shipments/components/VoidLabel';
import { CopyTrackNumberActionField } from 'pages/shipments/components/CopyTrackNumber';
import UpsActivationFlow from 'pages/shipmentAccounts/wizards/ups/UpsActivationFlow';
import { setPaymentAmount, removePaymentAmount } from 'store/actions/refillActions';
import mapRateEmailWarning from '../../utils/mapRateEmailWarning';
import { getDomesticCurrency } from 'services/helpers/currency';
import { getDomesticCountryCode } from 'services/helpers/countryCode';
import PaymentMethodsErrorDialog from 'components/layout/PaymentMethodsErrorDialog';

const useStyles = makeStyles((theme) => ({
  itemsContainer: {
    marginLeft: theme.spacing(6.8),
    marginBottom: theme.spacing(3),
  },
  quantity: {
    fontSize: '0.8rem',
    marginLeft: theme.spacing(1),
    paddingRight: theme.spacing(2),
  },
  sku: {
    fontSize: '0.8rem',
    marginLeft: theme.spacing(1.5),
  },
  name: {
    fontSize: '0.8rem',
    marginLeft: theme.spacing(2),
  },
  main: {
    display: 'flex',
  },
  content: {
    marginTop: 0,
    transition: theme.transitions.create('margin-top'),
    position: 'relative',
    overflow: 'visible',
    flex: '1 1 auto',
    [theme.breakpoints.down('xs')]: {
      boxShadow: 'none',
    },
  },
  root: {
    position: 'sticky',
    top: 0,
    zIndex: theme.zIndex.drawer,
  },

  container: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(2, 2, 0, 0),
    backgroundColor: theme.palette.background.paper,
  },

  caption: {
    fontSize: 14,
    color: theme.palette.primary.light,
    marginLeft: 20,
  },

  actions: {
    display: 'flex',
    marginLeft: 'auto',
    alignItems: 'flex-end',

    '& > *': {
      marginLeft: theme.spacing(2),
    },
  },
  totalPriceLabel: {
    fontWeight: 'bold',
    marginRight: theme.spacing(1),
  },
  priceBreakdownButton: {
    padding: 0,
    color: theme.palette.primary.light,
    '&.Mui-disabled': {
      color: theme.palette.primary.light,
      opacity: 0.5,
    },
  },
  priceBreakdownPopover: {
    width: 410,
    maxWidth: 'calc(100vw - 32px)',
    marginTop: theme.spacing(1),
    overflow: 'visible',
    borderRadius: 16,
    boxShadow: '0px 1px 24px rgba(0, 0, 0, 0.1)',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: -8,
      left: 136,
      width: 0,
      height: 0,
      borderLeft: '8px solid transparent',
      borderRight: '8px solid transparent',
      borderBottom: `8px solid ${theme.palette.background.paper}`,
    },
  },
  priceBreakdownContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    width: '100%',
    padding: theme.spacing(1.5, 2, 2.5),
    boxSizing: 'border-box',
    maxHeight: 'calc(100vh - 48px)',
    overflowY: 'auto',
    overflowX: 'hidden',
    borderRadius: 'inherit',
    '& > *': {
      flexShrink: 0,
    },
  },
  priceBreakdown: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    width: '100%',
  },
  priceBreakdownRows: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    width: '100%',
  },
  priceBreakdownRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    whiteSpace: 'nowrap',
  },
  priceBreakdownText: {
    color: theme.palette.text.primary,
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '20px',
  },
  priceBreakdownPrices: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  priceBreakdownDiscount: {
    color: theme.palette.secondary.contrastText,
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '20px',
    textDecoration: 'line-through',
  },
  taxesAndFees: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    width: '100%',
  },
  taxesAndFeesHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: 0,
    color: theme.palette.text.primary,
    whiteSpace: 'nowrap',
  },
  taxesAndFeesTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  taxesAndFeesIcon: {
    width: 16,
    height: 16,
    color: theme.palette.secondary.contrastText,
  },
  taxesAndFeesCollapse: {
    width: '100%',
  },
  taxesAndFeesDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    width: '100%',
    padding: theme.spacing(0, 1),
    boxSizing: 'border-box',
  },
  taxesAndFeesDetailText: {
    color: '#7F8299',
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: '20px',
    minWidth: 0,
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    '&:last-child': {
      flexShrink: 0,
      whiteSpace: 'nowrap',
      textAlign: 'right',
    },
  },
  taxesAndFeesRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: `${theme.spacing(1)}px`,
    width: '100%',
  },
  priceBreakdownTotalText: {
    color: theme.palette.common.black,
    fontSize: '16px',
    fontWeight: 700,
    lineHeight: '20px',
  },
  priceBreakdownDisclaimer: {
    color: '#969AB2',
    fontSize: '11px',
    fontWeight: 400,
    lineHeight: '20px',
  },
  labelsProcessingProgress: {
    marginRight: theme.spacing(1),
  },
  emailTracking: {
    height: 20,
    marginRight: 0,
    paddingTop: theme.spacing(1),
  },
  actionsContainer: {
    display: 'flex',
    alignSelf: 'flex-end',
    alignItems: 'flex-end',
    flexDirection: 'column',
    marginLeft: 'auto',
  },
  activateUpsButton: {
    padding: theme.spacing(1, 2.5),
    lineHeight: '16px',
  },
  packingSlipsButton: {
    lineHeight: '22px',
  },
  collapseContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
}));

const prepareRequestData = (lastShippingInfo, selectedIds, rates, fromPhoneOverride) => {
  const requestData = {
    companyLocationPresetId: lastShippingInfo[`${selectedIds[0] ?? rates[0].orderId}`].from.id,
    fromPhoneOverride,
    orderShipmentSelections: selectedIds
      .filter((id) => !rates.find((rate) => rate.orderId === id).isShipped)
      .map((id) => {
        const currentRate = rates.find((rate) => rate.orderId === id);
        const currentOrderInfo = lastShippingInfo[`${id}`];
        if (!currentRate.price.options.length) return undefined;
        return {
          orderId: id,
          insurance: currentOrderInfo.options.insurance,
          signature: currentOrderInfo.options.signature,
          packageInfo: { ...currentOrderInfo.packageInfo[0] },
          serviceCode: currentRate?.price.options[0].serviceClass.code,
          serviceName: currentRate?.price.options[0].serviceClass.name,
          shipmentAccount: currentRate?.price.accountInfo,
          price: currentRate.price.options[0].price,
          nonDiscountedPrice: currentRate.price.options[0].nonDiscountedPrice,
          shipmentDate: currentOrderInfo.shipmentDate,
        };
      }),
  };
  requestData.orderShipmentSelections = requestData.orderShipmentSelections.filter(
    (order) => !!order
  );
  return requestData;
};

const BuyLabelsButton = (props) => {
  const {
    selectedIds,
    lastShippingInfo,
    rates,
    buyLabels,
    isLoading,
    labelsProcessingText,
    recipientTrackingEmail,
    setIsLoading,
    setLabelsProcessingText,
    totalFilteredPrice,
    loadBalance,
  } = props;

  const classes = useStyles();
  const refill = useRefillDialog();
  const notify = useCustomNotify();
  const dispatch = useDispatch();
  const domesticCountryCode = getDomesticCountryCode();
  const { blockActionIfKycRequired, isCheckingKyc } = useKycActionGuard();

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [openErrorDialog, setOpenErrorDialog] = useState<boolean>(false);

  const createLabels = async () => {
    const requestData = prepareRequestData(
      lastShippingInfo,
      selectedIds,
      rates,
      lastShippingInfo.fromPhoneOverride
    );
    return await buyLabels({ ...requestData, recipientTrackingEmail });
  };

  const handleClick = async (e) => {
    try {
      e.stopPropagation();

      if (await blockActionIfKycRequired()) {
        return;
      }

      setLabelsProcessingText('Buying labels');
      setIsLoading(true);

      const { balance, autoRefillEnabled, failedCard, creditCardPaymentAllowed } =
        await loadBalance();

      if (
        balance < totalFilteredPrice &&
        (!autoRefillEnabled || (autoRefillEnabled && failedCard))
      ) {
        if (!autoRefillEnabled) {
          const paymentAmount = parseFloat((totalFilteredPrice - balance).toFixed(2));

          if (!paymentAmount) {
            setIsLoading(false);
            return;
          }

          dispatch(setPaymentAmount(paymentAmount));
        }

        if (domesticCountryCode === 'CA' && !creditCardPaymentAllowed) {
          setOpenErrorDialog(true);
          return;
        }

        refill(!autoRefillEnabled).then(() => {
          setIsLoading(false);
          dispatch(removePaymentAmount());
        });
      } else if (rates.some((rate) => rate.price.alerts?.length)) {
        setIsOpen(true);
        setIsLoading(false);
      } else {
        await createLabels();
      }
    } catch (error) {
      setIsLoading(false);
      notify(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleSubmit = () => {
    createLabels();
    setIsOpen(false);
  };

  const isButtonLoading = isLoading || isCheckingKyc;
  const buttonLabel = isCheckingKyc ? 'Checking verification' : labelsProcessingText;

  return (
    <>
      <Button
        {...props}
        color="primary"
        variant="contained"
        size="small"
        disabled={
          isButtonLoading ||
          rates?.every((rate) => rate.price?.alerts?.length) ||
          rates
            ?.filter((rate) => selectedIds?.includes(rate?.orderId))
            .some((rate) => rate?.price?.accountInfo?.marketingOnly)
        }
        onClick={handleClick}>
        {isButtonLoading ? (
          <>
            <CircularProgress size={12} className={classes.labelsProcessingProgress} />
            {buttonLabel}
          </>
        ) : (
          'Buy labels'
        )}
      </Button>
      <Dialog
        open={isOpen}
        confirmText={'Continue'}
        title={
          <>
            <div>Warning</div>
          </>
        }
        onCancel={handleClose}
        onConfirm={handleSubmit}>
        <Typography color="textPrimary">
          There are orders with errors (highlighted in red). To proceed without these orders, click
          on Continue. To go back and fix the errors, click Cancel.
        </Typography>
      </Dialog>

      <PaymentMethodsErrorDialog open={openErrorDialog} onClose={() => setOpenErrorDialog(false)} />
    </>
  );
};

const PrintLabelsButton = (props) => {
  const { rates, selectedIds, successfulShipments } = props;

  const dispatch = useDispatch();
  const dataProvider = useDataProvider();
  const notify = useCustomNotify();

  const handleClick = () => {
    if (!successfulShipments?.length || !selectedIds?.length) return;
    const labelsForReprint = successfulShipments
      ?.filter((shipment) => selectedIds?.includes(shipment.order.id))
      ?.map(({ id }) => id);
    dataProvider
      .create('groupLabels', {
        data: labelsForReprint,
      })
      .then(({ data: { url } }) => {
        dispatch(
          openDoc({
            name: 'Shipment Labels',
            url,
            documentType: 'shipmentLabel',
            labelMode: 'multi',
          })
        );
      })
      .catch((error: unknown) => {
        const errorMessage = error instanceof Error ? error.message : 'Error printing labels';
        notify(errorMessage, 'error');
      });
  };

  return (
    <Button
      {...props}
      color="primary"
      variant="contained"
      size="small"
      onClick={handleClick}
      disabled={rates
        .filter((rate) => selectedIds.includes(rate.orderId))
        .every((rate) => !rate.isShipped)}>
      Print labels
    </Button>
  );
};

const BulkActions = (props) => {
  const {
    selectedIds,
    lastShippingInfo,
    rates,
    buyLabels,
    isLoading,
    labelsProcessingText,
    successfulShipments,
    setRates,
    recipientTrackingEmail,
    setRecipientTrackingEmail,
    setIsLoading,
    setLabelsProcessingText,
  } = props;

  const classes = useStyles();
  const dataProvider = useDataProvider();
  const notify = useCustomNotify();
  const domesticCurrency = getDomesticCurrency();

  const [freeLabels, setFreeLabels] = useState<number | null>(null);
  const [balanceLoading, setBalanceLoading] = useState<boolean>(false);
  const [priceBreakdownAnchorEl, setPriceBreakdownAnchorEl] = useState<HTMLElement | null>(null);
  const [taxesAndFeesExpanded, setTaxesAndFeesExpanded] = useState<boolean>(true);

  const loadBalance = useCallback(async () => {
    try {
      setBalanceLoading(true);

      const { data: balanceSettings } = await dataProvider.get('billingBalance');

      const balance =
        balanceSettings.find((balance) => balance.currency === domesticCurrency)?.balance ?? 0;

      const freeLabels =
        balanceSettings.find((balance) => balance.currency === 'LABEL')?.balance ?? 0;

      const { data: billingSettings } = await dataProvider.get('billingSettings');

      let failedCard = '';

      const cardRefillEnabled =
        billingSettings.autoRefill?.enabled &&
        billingSettings.autoRefill.paymentMethodType === 'CARD';

      if (cardRefillEnabled && !failedCard) {
        const selectedCardId = billingSettings.autoRefill.defaultCardId;

        const selectedCard = billingSettings.cards?.find((card) => card.cardId === selectedCardId);

        failedCard = selectedCard?.failedLastTime ? selectedCard?.lastDigits : '';
      }

      const bankRefillEnabled =
        billingSettings.autoRefill?.enabled &&
        billingSettings.autoRefill.paymentMethodType === 'US_BANK_ACCOUNT';

      if (bankRefillEnabled && !failedCard) {
        const selectedAccountId = billingSettings.autoRefill.defaultUsBankAccount;

        const selectedAccount = billingSettings.usBankAccounts?.find(
          (account) => account.paymentMethodId === selectedAccountId
        );

        failedCard = selectedAccount?.failedLastTime ? selectedAccount?.lastDigits : '';
      }

      const creditCardPaymentAllowed = billingSettings?.creditCardPaymentAllowed;

      setFreeLabels(freeLabels);

      return {
        balance,
        autoRefillEnabled: cardRefillEnabled || bankRefillEnabled,
        failedCard,
        freeLabels,
        creditCardPaymentAllowed,
      };
    } catch (error: any) {
      const errorMessage = error?.message || 'Error loading balance';
      notify(errorMessage, 'error');
      return {};
    } finally {
      setBalanceLoading(false);
    }
  }, [dataProvider, domesticCurrency, notify]);

  useEffect(() => {
    loadBalance();
  }, [loadBalance]);

  const handleChange = () => {
    setRates((prev) => prev.map((rate) => mapRateEmailWarning(rate, !recipientTrackingEmail)));
    setRecipientTrackingEmail((prev) => !prev);
  };

  const {
    shipmentPrice,
    totalFilteredPrice,
    shippingPrice,
    nonDiscountedShippingPrice,
    commonPriceDetails,
    taxesAndFeesTotal,
    platformFee,
    labelFeeCents,
    priceBreakdownReady,
  } = useMemo(() => {
    let currentShipmentPrice = 0;
    let currentFilteredPrice = 0;
    let currentNonDiscountedPrice = 0;
    let hasCompleteNonDiscountedPrice = true;
    const labelFeePrice = rates?.[0]?.price?.options?.[0]?.labelFeePrice;
    const selectedRates = rates.filter((rate) => {
      const optionPrice = rate?.price?.options?.[0]?.price;

      return (
        selectedIds.includes(rate.orderId) &&
        optionPrice !== null &&
        optionPrice !== undefined &&
        Number.isFinite(Number(optionPrice))
      );
    });

    selectedRates.forEach((rate) => {
      const option = rate.price.options[0];
      const optionPrice = Number(option.price);
      const rawNonDiscountedPrice = option.nonDiscountedPrice;
      const nonDiscountedPrice = Number(rawNonDiscountedPrice);

      currentShipmentPrice += optionPrice;

      if (rate.price.accountInfo.rollo) {
        currentFilteredPrice += optionPrice;
      }

      if (
        rawNonDiscountedPrice !== null &&
        rawNonDiscountedPrice !== undefined &&
        Number.isFinite(nonDiscountedPrice)
      ) {
        currentNonDiscountedPrice += nonDiscountedPrice;
      } else {
        hasCompleteNonDiscountedPrice = false;
      }
    });

    const priceDetailMaps = selectedRates.map((rate) => {
      const detailTotals = new Map<string, number>();
      const priceDetails = rate.price.options[0].priceDetails;

      if (!Array.isArray(priceDetails)) return detailTotals;

      priceDetails.forEach((detail) => {
        const description = detail?.description;
        const rawCost = detail?.cost;
        const cost = Number(rawCost);

        if (
          typeof description !== 'string' ||
          !description ||
          rawCost === null ||
          rawCost === undefined ||
          !Number.isFinite(cost)
        ) {
          return;
        }

        detailTotals.set(description, (detailTotals.get(description) ?? 0) + cost);
      });

      return detailTotals;
    });

    const commonPriceDetails: { description: string; cost: number }[] = [];
    const firstPriceDetails = selectedRates?.[0]?.price?.options?.[0]?.priceDetails;
    const addedDescriptions = new Set<string>();

    if (Array.isArray(firstPriceDetails)) {
      firstPriceDetails.forEach((detail) => {
        const description = detail?.description;

        if (
          typeof description !== 'string' ||
          !description ||
          addedDescriptions.has(description) ||
          !priceDetailMaps.length ||
          !priceDetailMaps.every((detailTotals) => detailTotals.has(description))
        ) {
          return;
        }

        const cost = priceDetailMaps.reduce(
          (total, detailTotals) => total + (detailTotals.get(description) ?? 0),
          0
        );

        commonPriceDetails.push({
          description,
          cost: Number(cost.toFixed(2)),
        });
        addedDescriptions.add(description);
      });
    }

    const commonPriceDetailsTotal = commonPriceDetails.reduce(
      (total, detail) => total + detail.cost,
      0
    );

    const totalPaidLabels =
      freeLabels === null
        ? 0
        : rates?.length < selectedIds?.length
        ? 0
        : selectedIds?.length > freeLabels
        ? selectedIds?.length - freeLabels
        : 0;

    const totalFee = totalPaidLabels * (labelFeePrice ?? 0);

    const shipmentPrice = Number((currentShipmentPrice + totalFee).toFixed(2));
    const totalFilteredPrice = Number((currentFilteredPrice + totalFee).toFixed(2));
    const shippingPrice = Number((currentShipmentPrice - commonPriceDetailsTotal).toFixed(2));
    const nonDiscountedShippingPrice = Number(currentNonDiscountedPrice.toFixed(2));
    const taxesAndFeesTotal = Number((commonPriceDetailsTotal + totalFee).toFixed(2));
    const priceBreakdownReady =
      !isLoading && !balanceLoading && freeLabels !== null && selectedRates.length > 0;

    return {
      shipmentPrice,
      totalFilteredPrice,
      shippingPrice,
      nonDiscountedShippingPrice:
        hasCompleteNonDiscountedPrice && nonDiscountedShippingPrice > shippingPrice
          ? nonDiscountedShippingPrice
          : null,
      commonPriceDetails,
      taxesAndFeesTotal,
      platformFee: Number(totalFee.toFixed(2)),
      labelFeeCents: Number(((labelFeePrice ?? 0) * 100).toFixed(2)),
      priceBreakdownReady,
    };
  }, [rates, selectedIds, freeLabels, isLoading, balanceLoading]);

  useEffect(() => {
    if (!priceBreakdownReady) setPriceBreakdownAnchorEl(null);
  }, [priceBreakdownReady]);

  const priceBreakdownOpen = !!priceBreakdownAnchorEl && priceBreakdownReady;

  const handlePriceBreakdownOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (!priceBreakdownReady) return;

    setTaxesAndFeesExpanded(true);
    setPriceBreakdownAnchorEl(event.currentTarget);
  };

  const handlePriceBreakdownClose = () => {
    setPriceBreakdownAnchorEl(null);
  };

  return (
    <div className={classes.root} data-datagrid-bulk-actions="true">
      <Collapse in={!!selectedIds.length || isLoading} className={classes.collapseContainer}>
        <div className={classes.container}>
          <Typography variant="body1" className={classes.totalPriceLabel}>
            Total: {!balanceLoading ? formatPrice(shipmentPrice) : 'Loading...'}
          </Typography>
          <IconButton
            aria-controls={priceBreakdownOpen ? 'group-price-breakdown' : undefined}
            aria-expanded={priceBreakdownOpen}
            aria-haspopup="dialog"
            aria-label="Show price breakdown"
            className={classes.priceBreakdownButton}
            disabled={!priceBreakdownReady}
            onClick={handlePriceBreakdownOpen}
            size="small">
            <InfoOutlined fontSize="small" />
          </IconButton>
          <div className={classes.caption}>
            <b>{selectedIds.length}</b> selected
          </div>
          <div className={classes.actionsContainer}>
            <div className={classes.actions}>
              <BulkPrintPackingSlipsButton
                primary={false}
                ids={selectedIds}
                type="orders"
                isLoading={isLoading}
                className={classes.packingSlipsButton}
              />
              {successfulShipments?.length ? (
                <PrintLabelsButton
                  rates={rates}
                  selectedIds={selectedIds}
                  successfulShipments={successfulShipments}
                  setIsLoading={setIsLoading}
                />
              ) : (
                <BuyLabelsButton
                  selectedIds={selectedIds}
                  lastShippingInfo={lastShippingInfo}
                  rates={rates}
                  buyLabels={buyLabels}
                  isLoading={isLoading}
                  labelsProcessingText={labelsProcessingText}
                  setRates={setRates}
                  recipientTrackingEmail={recipientTrackingEmail}
                  setIsLoading={setIsLoading}
                  setLabelsProcessingText={setLabelsProcessingText}
                  totalFilteredPrice={totalFilteredPrice}
                  loadBalance={loadBalance}
                />
              )}
            </div>
            {!successfulShipments?.length && (
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    onClick={handleChange}
                    checked={recipientTrackingEmail}
                    disabled={isLoading}
                  />
                }
                label="Email tracking to recipient"
                className={classes.emailTracking}
              />
            )}
          </div>
        </div>
      </Collapse>
      <Popover
        id="group-price-breakdown"
        open={priceBreakdownOpen}
        anchorEl={priceBreakdownAnchorEl}
        onClose={handlePriceBreakdownClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 144,
        }}
        classes={{ paper: classes.priceBreakdownPopover }}>
        <div
          aria-label="Grouped shipment price breakdown"
          className={classes.priceBreakdownContent}
          role="dialog"
          tabIndex={0}>
          <div className={classes.priceBreakdown}>
            <div className={classes.priceBreakdownRows}>
              <div className={classes.priceBreakdownRow}>
                <Typography className={classes.priceBreakdownText}>Shipping</Typography>
                <div className={classes.priceBreakdownPrices}>
                  {nonDiscountedShippingPrice !== null && (
                    <Typography className={classes.priceBreakdownDiscount}>
                      {formatPrice(nonDiscountedShippingPrice)}
                    </Typography>
                  )}
                  <Typography className={classes.priceBreakdownText}>
                    {formatPrice(shippingPrice)}
                  </Typography>
                </div>
              </div>

              <div className={classes.taxesAndFees}>
                <ButtonBase
                  aria-controls="group-taxes-and-fees-details"
                  aria-expanded={taxesAndFeesExpanded}
                  aria-label={`${taxesAndFeesExpanded ? 'Collapse' : 'Expand'} Taxes & fees`}
                  className={classes.taxesAndFeesHeader}
                  onClick={() => setTaxesAndFeesExpanded((expanded) => !expanded)}
                  type="button">
                  <div className={classes.taxesAndFeesTitle}>
                    <Typography className={classes.priceBreakdownText}>Taxes &amp; fees</Typography>
                    {taxesAndFeesExpanded ? (
                      <ExpandLess className={classes.taxesAndFeesIcon} />
                    ) : (
                      <ExpandMore className={classes.taxesAndFeesIcon} />
                    )}
                  </div>
                  <Typography className={classes.priceBreakdownText}>
                    {formatPrice(taxesAndFeesTotal)}
                  </Typography>
                </ButtonBase>

                <Collapse
                  className={classes.taxesAndFeesCollapse}
                  in={taxesAndFeesExpanded}
                  timeout="auto"
                  unmountOnExit>
                  <div className={classes.taxesAndFeesDetails} id="group-taxes-and-fees-details">
                    {commonPriceDetails.map((detail) => (
                      <div className={classes.taxesAndFeesRow} key={detail.description}>
                        <Typography className={classes.taxesAndFeesDetailText}>
                          {detail.description}
                        </Typography>
                        <Typography className={classes.taxesAndFeesDetailText}>
                          {formatPrice(detail.cost)}
                        </Typography>
                      </div>
                    ))}
                    <div className={classes.taxesAndFeesRow}>
                      <Typography className={classes.taxesAndFeesDetailText}>
                        Platform fee
                      </Typography>
                      <Typography className={classes.taxesAndFeesDetailText}>
                        {formatPrice(platformFee)}
                      </Typography>
                    </div>
                  </div>
                </Collapse>
              </div>
            </div>

            <div className={classes.priceBreakdownRow}>
              <Typography className={classes.priceBreakdownTotalText}>Total</Typography>
              <Typography className={classes.priceBreakdownTotalText}>
                {formatPrice(shipmentPrice)}
              </Typography>
            </div>
          </div>

          {freeLabels !== null && freeLabels <= 0 && (
            <Typography className={classes.priceBreakdownDisclaimer}>
              Each tracking number you generate will cost {labelFeeCents}{' '}
              {labelFeeCents === 1 ? 'cent' : 'cents'} which helps us to maintain the service. It is
              waived for your first 200 shipments. This nominal fee is easily covered by savings
              from Rollo’s discounted shipping rates (up to 90% off)
            </Typography>
          )}
        </div>
      </Popover>
    </div>
  );
};

const ShipmentMenuField = (props) => {
  return (
    <ActionField header={ColumnsConfigButton} textAlign="center">
      <ReturnLabelActionField {...props} />
      <ReprintLabelActionField {...props} />
      <PrintPackingSlipActionField {...props} />
      <VoidLabelActionField {...props} />
      <CopyTrackNumberActionField {...props} />
    </ActionField>
  );
};

const ShipmentActionsField = (props) => {
  const {
    initData,
    packageTypes,
    carriers,
    onSubmit,
    isLoading,
    textAlign,
    hide,
    updateError,
    setUpdateError,
    successfulShipments,
    record,
    deliveryTimes,
    setRates,
  } = props;

  const shipmentRecord = successfulShipments?.find(
    (shipment) => shipment.order.id === record?.orderId
  );

  return record?.isShipped && shipmentRecord ? (
    <ShipmentMenuField
      {...props}
      record={shipmentRecord}
      setRates={setRates}
      voidedLabel={record.voidedLabel}
    />
  ) : (
    <RateEditField
      initData={initData}
      packageTypes={packageTypes}
      carriers={carriers}
      onSubmit={onSubmit}
      isLoading={isLoading}
      textAlign={textAlign}
      hide={hide}
      updateError={updateError}
      setUpdateError={setUpdateError}
      record={record}
      deliveryTimes={deliveryTimes}
    />
  );
};

const ActivateUpsField = (props) => {
  const {
    record: {
      price: { accountInfo },
    },
    match,
    history,
    location,
    setRates,
  } = props;

  const [upsActivationModalOpen, setActivationModalOpen] = useState<boolean>(false);
  const [upsId, setUpsId] = useState<string>('');
  const classes = useStyles();

  const onUpsActivation = (item) => {
    setActivationModalOpen(true);
    setUpsId(accountInfo.id);
  };

  const onUpsAccountActivated = () => {
    setActivationModalOpen(false);
    setRates((prev) =>
      prev.map((rate) => ({
        ...rate,
        price: {
          ...rate.price,
          accountInfo: {
            ...rate.price.accountInfo,
            marketingOnly: false,
          },
        },
      }))
    );
  };

  return (
    <>
      {accountInfo?.marketingOnly && (
        <Button
          className={classes.activateUpsButton}
          color="primary"
          variant="contained"
          size="small"
          onClick={onUpsActivation}>
          Activate UPS
        </Button>
      )}
      <UpsActivationFlow
        upsId={upsId}
        open={upsActivationModalOpen}
        onCancel={() => setActivationModalOpen(false)}
        onConfirm={onUpsAccountActivated}
        match={match}
        history={history}
        location={location}
        basePath={match.params.url}
      />
    </>
  );
};

const GroupedPrices = (props) => {
  const {
    rates,
    lastShippingInfo,
    carriers,
    packageTypes,
    setRates,
    setLastShippingInfo,
    buyLabels,
    isLoading,
    setIsLoading,
    selectedIds,
    setSelectedIds,
    labelsProcessingText,
    setLabelsProcessingText,
    successfulShipments,
    deliveryTimes,
    recipientTrackingEmail,
    setRecipientTrackingEmail,
    setShowFixModal,
    requestRateEdit,
  } = props;
  const classes = useStyles();
  const dataProvider = useDataProvider();
  const notify = useCustomNotify();

  const [updateError, setUpdateError] = useState<string>('');

  const handleFixIt = () => {
    setShowFixModal(true);
  };

  const handleRateEdit = (data, rateId, recipient) => {
    const {
      comments,
      currency,
      email,
      groupId,
      id,
      items,
      itemsSummary,
      lastUpdatedDate,
      marketAccount,
      marketplaceOrderId,
      note,
      orderStatus,
      purchaseDate,
      sellerOrderId,
      shippingAddress,
      shippingPaid,
      shippingService,
      tax,
      total,
      from,
      requestCarrier,
      selectedService,
      options,
      shipmentDate,
      packageInfo,
      selectedCarriers,
      taxIds,
      reference1,
      reference2,
    } = data;

    const packageType = packageTypes.find((item) => item.code === packageInfo[0].type);

    const rateEditData = {
      carriers: packageType.provider
        ? [packageType.provider]
        : deliveryTimes.some((time) => time.code === selectedService)
        ? selectedCarriers
        : requestCarrier,
      companyLocationPresetId: from.id,
      deliveryServiceCode: selectedService,
      insurance: options.insurance,
      signature: options.signature,
      shipmentDate: shipmentDate,
      packageInfo: {
        ...packageInfo[0],
      },
      orders: [rateId],
      fromPhoneOverride: lastShippingInfo.fromPhoneOverride,
    };

    const recipientEditData = {
      comments,
      currency,
      email,
      groupId,
      id,
      items,
      itemsSummary,
      lastUpdatedDate,
      marketAccount,
      marketplaceOrderId,
      note,
      orderStatus,
      purchaseDate,
      sellerOrderId,
      shippingAddress,
      shippingPaid,
      shippingService,
      tax,
      total,
      taxIds,
      reference1,
      reference2,
    };
    setIsLoading(true);
    setUpdateError('');

    if (!isEqual(recipientEditData, recipient)) {
      return dataProvider
        .update('orders', { id: rateId, data: recipientEditData })
        .catch((error: unknown) => {
          const errorMessage = error instanceof Error ? error.message : 'Error updating order';
          setUpdateError(errorMessage);
          setIsLoading(false);
          notify(errorMessage, 'error');
          throw error;
        })
        .then(() => requestRateEdit(rateEditData, rateId))
        .then(() => setLastShippingInfo((prev) => ({ ...prev, [`${rateId}`]: data })));
    } else {
      return requestRateEdit(rateEditData, rateId)
        .then(() => setLastShippingInfo((prev) => ({ ...prev, [`${rateId}`]: data })))
        .catch((error: unknown) => {
          setIsLoading(false);
          const errorMessage = error instanceof Error ? error.message : 'Error updating order';
          notify(errorMessage, 'error');
          throw error;
        });
    }
  };

  return (
    <>
      <div className={classes.main}>
        <Card className={classes.content}>
          <BulkActions
            {...props}
            selectedIds={selectedIds}
            lastShippingInfo={lastShippingInfo}
            rates={rates}
            buyLabels={buyLabels}
            isLoading={isLoading}
            labelsProcessingText={labelsProcessingText}
            successfulShipments={successfulShipments}
            setRates={setRates}
            recipientTrackingEmail={recipientTrackingEmail}
            setRecipientTrackingEmail={setRecipientTrackingEmail}
            setIsLoading={setIsLoading}
            setLabelsProcessingText={setLabelsProcessingText}
          />
          <Datagrid
            {...props}
            data={keyBy(rates, 'orderId')}
            ids={useMemo(() => rates.map(({ orderId }) => orderId), [rates])}
            currentSort={{}}
            basePath="orders"
            resource="orders"
            selectedIds={selectedIds}
            hasBulkActions
            onToggleItem={(currentId) => {
              selectedIds.includes(currentId)
                ? setSelectedIds((prev) => prev.filter((id) => id !== currentId))
                : setSelectedIds((prev) => [...prev, currentId]);
            }}
            onSelect={(ids) => setSelectedIds(ids)}>
            <OrderIdField label="Order No." source="marketplaceOrSellerOrderId" sortable={false} />
            <StoreField
              label="Carrier"
              source="store"
              tooltipSource="price.accountInfo.alias"
              logoSource="price.accountInfo.provider.logoUrl"
            />
            <ShippingServiceField label="Service" source="price" onClick={handleFixIt} />
            <DeliveryTimeField
              label="Delivery Time"
              source="price.options[0].deliveryDays"
              dateSource="price.options[0].estimatedArrivalDate"
              timeSource="price.options[0].estimatedArrivalTime"
            />
            <PriceField label="Price" source="price.options[0].price" />
            <RateStatusField label="Status" textAlign="center" />
            <ActivateUpsField {...props} setRates={setRates} />
            <ShipmentActionsField
              initData={lastShippingInfo}
              packageTypes={packageTypes}
              carriers={carriers}
              onSubmit={handleRateEdit}
              isLoading={isLoading}
              textAlign="right"
              hide={rates.some((rate) => rate.isShipped)}
              updateError={updateError}
              setUpdateError={setUpdateError}
              successfulShipments={successfulShipments}
              deliveryTimes={deliveryTimes}
              setRates={setRates}
            />
          </Datagrid>
        </Card>
      </div>
    </>
  );
};

export default memo(GroupedPrices);
